import {AxiosError, type AxiosResponse} from 'axios';
import {type MutationTree} from 'vuex';
import {isDeveloper, setCssColorVars} from '../utils/portuna';

function parseError(response: AxiosResponse)
{
    return Object.assign({
        time: Date.now(),
        url: response.config.url,
        statusCode: response.status,
        message: 'Sollte es sich hierbei um einen Fehler handeln, wenden Sie sich bitte an Ihren Ansprechpartner.',
        error: 'dispatch-error',
        status: 'ERROR',
        trace: null,
        file: null,
        line: null,
    }, response.data);
}

const mutations: MutationTree<RootState> = {
    topMessageAdd(state, data)
    {
        state.topMessages.push(data);
    },
    topMessageShift(state)
    {
        state.topMessages.shift();
    },
    flashMessageAdd(state, data)
    {
        state.flashMessages.push(data);
    },
    flashMessageRemove(state, key)
    {
        state.flashMessages.splice(key, 1);
    },
    flashMessageReset(state)
    {
        state.flashMessages = [];
    },
    loader(state, data)
    {
        state.loader = data;
    },
    login(state)
    {
        state.loggedIn = true;
    },
    addError(state: RootState, error)
    {
        state.errors.push(parseError(error.response));
    },
    setCalendarHourHeight(state: RootState, calendarHourHeight: number)
    {
        state.calendarHourHeight = calendarHourHeight;
    },
    setError(state: RootState, error: ErrorData | AxiosError | AxiosResponse | number | null)
    {
        if (error)
        {
            if ('number' === typeof error)
            {
                error = state.errors[error];
            }
            else if ('undefined' !== typeof (error as AxiosError).response)
            {
                error = parseError((error as AxiosError).response as AxiosResponse);
            }
            // @ts-ignore
            else if ('undefined' !== typeof error.data && 'undefined' !== typeof error.data.status && 'undefined' !== error.data.message)
            {
                error = parseError(error as AxiosResponse);
            }
        }

        state.error = error as ErrorData;
    },
    updateDebug(state, data)
    {
        if (isDeveloper())
        {
            data.time = Date.now();
            if (state._debug)
            {
                state._debugHistory.push(Object.assign({}, data));
            }
            state._debug = data;
        }
    },
    setLoading(state, active)
    {
        state.pageLoading = active;
    },
    setLayoutDataLoaded(state, payload)
    {
        state.layoutDataLoaded = payload;
    },
    setLayoutData(state, data)
    {
        for (let key in data)
        {
            if (data.hasOwnProperty(key) && state.layoutData.hasOwnProperty(key))
            {
                state.layoutData[key] = data[key];
            }
        }

        state.layoutDataLoaded = true;

        if (data.colors)
        {
            setCssColorVars(data.colors);
        }
    },
    bookmarkSetId(state, id)
    {
        state.bookmark.id = id;
    },
    bookmarkSetList(state, bookmarks)
    {
        state.bookmark.list = bookmarks;
    },
    updateHeadline(state, newHeadline)
    {
        state.headline = newHeadline;
    },
    updatePortunaBoxOpened(state, payload)
    {
        state.portunaBoxOpened[payload.group] = payload.uid;
    },
    removePortunaBoxOpened(state, payload)
    {
        delete state.portunaBoxOpened[payload.group];
    },
    updateTabBar(state, payload)
    {
        state.tabBar[payload.group] = payload.data;
    },
    removeTabBar(state, payload)
    {
        delete state.tabBar[payload.group];
    },
    updateFoldContainer(state, payload)
    {
        state.foldContainer[payload.group] = payload.open;
    },
    hideApp(state, hideApp)
    {
        state.hideApp = hideApp;
    },
    setTileMenu(state, tileMenu)
    {
        state.tileMenu = tileMenu;
    },
    addModuleMenu(state, payload)
    {
        state.moduleMenus[payload.resource] = {name: payload.name, menu: payload.menu};
    },
    resetModuleMenus(state)
    {
        state['moduleMenus'] = {};
    },
    clearModuleMenu(state, resource)
    {
        delete state.moduleMenus[resource];
    },
    setModuleMenu(state, menu)
    {
        state.moduleMenu = menu;
    },
    personSet(state, payload)
    {
        if (typeof state[payload.type] === 'undefined' || state[payload.type] === null)
        {
            state[payload.type] = {};
        }

        state[payload.type][payload.id] = payload.data;
    },
    tooltipSet(state, payload)
    {
        state.tooltips[payload.type][payload.key] = {
            tooltip: payload.tooltip,
            lastUpdate: Date.now(),
        };
    },
    tooltipDelete(state, payload)
    {
        if (typeof state.tooltips[payload.type] !== 'undefined' && typeof state.tooltips[payload.type][payload.key] !== 'undefined')
        {
            delete state.tooltips[payload.type][payload.key];
        }
    },
    showToolbar(state, status)
    {
        state.showToolbar = status;
    },
    toggleEmployeeFirstDay(state)
    {
        state.layoutData.employee_first_day = !state.layoutData.employee_first_day;
    },
    addPopup(state, {
        id,
        popup,
    })
    {
        state.popups[id] = popup;
    },
    deletePopup(state, id)
    {
        delete state.popups[id];
    },
    changePopup(state, {
        id,
        values,
    })
    {
        if (!state.popups.hasOwnProperty(id))
        {
            console.error('cant change values because popup with id ' + id + ' don\'t exists');
        }

        for (const [key, value] of Object.entries(values as Record<string, any>))
        {
            let object     = state.popups[id];
            const keyParts = key.split('.');

            // To change a value in a nested object iterate over every Part
            // Key must be in this format: componentProps.extraData.test
            for (const keyPart of keyParts.slice(0, -1))
            {
                if (object.hasOwnProperty(keyPart))
                {
                    object = object[keyPart];
                }
                else
                {
                    console.error('cant change ' + key + ', because path don\'t exists');
                    object = undefined;
                    break;
                }
            }
            if (object && keyParts.at(-1))
            {
                object[keyParts.at(-1)] = value;
            }
        }
    },
};

export default mutations;